import Strings from "utils/strings"

/**
 * Status Utilities
 *
 * Centralizes status configuration in two self-contained sections:
 *   1. Internal status  — orders & routes (Ant Design Tag colors + labels)
 *   2. External delivery — partner statuses (hex colors, reduced set, labels)
 */

// ============================================================================
// 1. INTERNAL STATUS (orders & routes)
// ============================================================================

// All possible status values across orders and routes
export type Status =
	// Order-specific statuses
	| "registered"
	| "address_confirmation"
	| "picking"
	| "packing"
	| "processing"
	| "aggregation"
	| "loading"
	| "ready_to_van"
	| "dispatching"
	| "on_transit"
	| "delivered"
	| "not_delivered"
	| "cancelled"
	| "returned"
	| "dispute"
	| "billing"
	| "partial_delivered" // For bundled orders
	| "mixed_failed" // For bundled orders with mixed failures
	| "finished" // For routes that have completed all deliveries

// Colors for the Ant Design Tag component, keyed by status.
export const STATUS_COLORS: Record<Status, string> = {
	// Order flow statuses
	registered: "default",
	address_confirmation: "gold",
	picking: "cyan",
	packing: "geekblue",
	processing: "blue",

	// Shared statuses (Routes and Orders)
	aggregation: "cyan",
	loading: "blue",
	ready_to_van: "green",
	dispatching: "orange",
	on_transit: "purple",

	// Delivery statuses
	delivered: "success",
	not_delivered: "error",
	cancelled: "error",
	returned: "warning",
	dispute: "magenta",
	billing: "lime",

	// Mixed (for bundled orders)
	partial_delivered: "volcano",
	mixed_failed: "error",

	// Route-specific status
	finished: "geekblue",
}

/** Tag color for any status (works for both orders and routes). */
export const getStatusColor = (status: Status): string => {
	return STATUS_COLORS[status] || "default"
}

/** Localized label for any status (works for both orders and routes). */
export const getStatusLabel = (status: Status): string => {
	switch (status) {
		// Order statuses
		case "registered":
			return Strings.orders.registered
		case "address_confirmation":
			return Strings.orders.addressConfirmation
		case "picking":
			return Strings.orders.picking
		case "packing":
			return Strings.orders.packing
		case "processing":
			return Strings.orders.processing

		// Shared statuses
		case "aggregation":
			return Strings.orders.aggregation
		case "loading":
			return Strings.orders.readyToLoad
		case "ready_to_van":
			return Strings.orders.readyToVan
		case "dispatching":
			return Strings.orders.dispatching
		case "on_transit":
			return Strings.orders.on_transit

		// Delivery statuses
		case "delivered":
			return Strings.orders.orderDelivered
		case "not_delivered":
			return Strings.orders.orderNotDelivered
		case "cancelled":
			return Strings.orders.orderCancelled
		case "returned":
			return Strings.orders.orderReturned
		case "dispute":
			return Strings.orders.dispute
		case "billing":
			return Strings.orders.billing

		// Bundle mixed status
		case "partial_delivered":
			return Strings.orders.partialDelivered
		case "mixed_failed":
			return Strings.orders.mixedFailed

		case "finished":
			return Strings.routes.finished

		default:
			return "-"
	}
}

// ============================================================================
// 2. EXTERNAL DELIVERY STATUS (partner deliveries)
// ============================================================================

// All possible statuses for external deliveries
export type ExternalDeliveryStatus =
	| "pending"
	| "assigned"
	| "en_route_pickup"
	| "arriving_pickup"
	| "pickup_complete"
	| "out_for_delivery"
	| "arriving_dropoff"
	| "delivered"
	| "returning"
	| "not_delivered"
	| "cancelled"

/**
 * Hex colors for external delivery statuses. Unlike STATUS_COLORS these are
 * raw hex (not Ant Design preset names) so they can also be used for Google
 * Maps marker fills on the delivery map.
 */
export const EXTERNAL_STATUS_COLORS: Record<ExternalDeliveryStatus, string> = {
	pending: "#8c8c8c",
	assigned: "#1677ff",
	en_route_pickup: "#4096ff",
	arriving_pickup: "#69b1ff",
	pickup_complete: "#13c2c2",
	out_for_delivery: "#fa8c16",
	arriving_dropoff: "#ffa940",
	delivered: "#52c41a",
	returning: "#d4b106",
	not_delivered: "#f5222d",
	cancelled: "#d9d9d9",
}

/** Hex color for any external delivery status, with a neutral fallback. */
export const getExternalStatusColorHex = (
	status: ExternalDeliveryStatus
): string => EXTERNAL_STATUS_COLORS[status] ?? "#8c8c8c"

/**
 * The "important" subset of external statuses shown by default on reduced
 * timelines. Granular states outside this set are hidden unless the user opts
 * to see all of them.
 */
export const REDUCED_EXTERNAL_STATUSES: ExternalDeliveryStatus[] = [
	"assigned",
	"pickup_complete",
	"arriving_dropoff",
	"delivered",
	"returning",
	"not_delivered",
	"cancelled",
]

export const isReducedExternalStatus = (
	status: ExternalDeliveryStatus
): boolean => REDUCED_EXTERNAL_STATUSES.includes(status)

/** Localized label for any external delivery status. */
export const getExternalStatusLabel = (
	status: ExternalDeliveryStatus
): string => {
	switch (status) {
		case "pending":
			return Strings.routes.externalDeliveryStatusPending
		case "assigned":
			return Strings.routes.externalDeliveryStatusAssigned
		case "en_route_pickup":
			return Strings.routes.externalDeliveryStatusEnRoutePickup
		case "arriving_pickup":
			return Strings.routes.externalDeliveryStatusArrivingPickup
		case "pickup_complete":
			return Strings.routes.externalDeliveryStatusPickupComplete
		case "out_for_delivery":
			return Strings.routes.externalDeliveryStatusOutForDelivery
		case "arriving_dropoff":
			return Strings.routes.externalDeliveryStatusArrivingDropoff
		case "delivered":
			return Strings.routes.externalDeliveryStatusDelivered
		case "returning":
			return Strings.routes.externalDeliveryStatusReturning
		case "not_delivered":
			return Strings.routes.externalDeliveryStatusNotDelivered
		case "cancelled":
			return Strings.routes.externalDeliveryStatusCancelled
		default:
			return status
	}
}
